import { useEffect, useState } from 'react'
import Modal from '../../../shared/ui/overlay/Modal/Modal'
import { minutesBetween } from '../../../shared/ui/controls/TimeStepper/quarterStep'
import { formatDurationDe } from '../../../shared/lib/durationDe'
import {
  changedFields,
  formatTimeDe,
  nowInstant,
  sessionTimesFrom,
  type SessionTimes,
} from '../model/workSessionTime'
import { SessionTimesDisclosure } from './SessionTimesDisclosure'

interface Props {
  show: boolean
  setShow: (show: boolean) => void
  /** The recorded work end — the departure is measured from it. */
  actualEndsAt: string | null
  isPending: boolean
  /** Rendered as role="alert"; null hides it. */
  errorMessage: string | null
  onSubmit: (dto: { departureEndedAt?: string }) => void
  /** Fired when the modal opens — the wrappers reset their mutation state here. */
  onOpen?: () => void
}

/**
 * "Abfahrt beenden" — one tap on the primary button, and a collapsed correction behind it.
 *
 * It gets its own correction point because the departure instant is stamped AFTER the sign-off:
 * the sign-off sheet could not have reviewed it. Forgetting this tap until the next morning is
 * common (he is already home), and without a correction here that would file a 14-hour
 * departure the worker cannot fix.
 */
export function EndDepartureModalView({
  show,
  setShow,
  actualEndsAt,
  isPending,
  errorMessage,
  onSubmit,
  onOpen,
}: Props) {
  const [times, setTimes] = useState<SessionTimes | null>(null)
  const [initial, setInitial] = useState<SessionTimes | null>(null)
  const [showCorrection, setShowCorrection] = useState(false)

  // Deliberately a plain effect rather than `useResetOnShow`: that hook keeps its `reset` in the
  // dependency array, and an inline closure there would re-run on every render — with setState
  // inside, that is a loop.
  useEffect(() => {
    if (!show) return
    onOpen?.()
    const start = sessionTimesFrom(
      { travelStartedAt: null, actualWorkStartsAt: null, actualEndsAt, departureEndedAt: null },
      { field: 'departureEndedAt', value: nowInstant() },
    )
    setInitial(start)
    setTimes(start)
    setShowCorrection(false)
    // `onOpen` is a fresh closure each render; opening is what must trigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, actualEndsAt])

  const handleSubmit = () => {
    if (!times || !initial) return
    const changed = changedFields(initial, times, ['departureEndedAt'])
    onSubmit(changed)
  }

  const departureMinutes =
    actualEndsAt && times?.departureEndedAt
      ? minutesBetween(actualEndsAt, times.departureEndedAt)
      : null

  return (
    <Modal
      show={show}
      setShow={setShow}
      title="Abfahrt beenden"
      submitButton={{ title: 'Abfahrt beenden', isPending, onSubmit: handleSubmit }}
    >
      <div className="flex w-full flex-col gap-4">
        <p className="text-sm text-gray-600">
          {actualEndsAt ? (
            <>
              Arbeit beendet um {formatTimeDe(actualEndsAt)} Uhr
              {departureMinutes !== null && (
                <>
                  <span className="mx-1.5" aria-hidden="true">·</span>
                  Abfahrt:{' '}
                  <span className="font-medium text-gray-900 tabular-nums">
                    {formatDurationDe(departureMinutes)}
                  </span>
                </>
              )}
            </>
          ) : (
            'Die Abfahrtszeit wird beim Beenden erfasst.'
          )}
        </p>

        {times && (
          <SessionTimesDisclosure
            times={times}
            editable={['departureEndedAt']}
            onChange={setTimes}
            open={showCorrection}
            onToggle={() => setShowCorrection((v) => !v)}
          />
        )}

        {errorMessage && (
          <p role="alert" className="text-sm font-medium text-red-500">
            {errorMessage}
          </p>
        )}
      </div>
    </Modal>
  )
}
